import type { MaskitoOptions } from "@maskito/core";
import {
  maskitoDateOptionsGenerator,
  maskitoWithPlaceholder,
} from "@maskito/kit";
import { dayjs, FORMAT_TYPEABLE_DATE_PLACEHOLDER } from "@/core/utils/date";

/**
 * Builds the mask every typeable date field wears, so the single-date picker and
 * the range picker cannot drift into two different ideas of what a date looks
 * like.
 *
 * A factory rather than a shared constant: `maskitoWithPlaceholder` closes over
 * mutable state, so one instance handed to two inputs would have them writing
 * over each other's idea of what is typed. Call it once per input, via useMemo.
 *
 * The placeholder is what puts the separators on screen while you type. The bare
 * date mask is lazy and adds " / " only once you type the digit after it, which
 * leaves you looking at "12" wondering whether the field took it.
 */
export const createTypeableDateMask = (): MaskitoOptions => {
  const date = maskitoDateOptionsGenerator({
    mode: "mm/dd/yyyy",
    separator: " / ",
  });
  const placeholder = maskitoWithPlaceholder(FORMAT_TYPEABLE_DATE_PLACEHOLDER);

  return {
    ...date,
    plugins: [...date.plugins, ...placeholder.plugins],
    // The placeholder comes off before the date logic reads the value, and goes
    // back on after that logic has had its say.
    preprocessors: [...placeholder.preprocessors, ...date.preprocessors],
    postprocessors: [...date.postprocessors, ...placeholder.postprocessors],
  };
};

/**
 * Reads what someone typed into a masked date field.
 *
 * Non-digits are stripped first, so this accepts the masked text
 * ("09 / 03 / 2026"), bare digits ("09032026"), and a half-typed value still
 * carrying its placeholder ("09 / 03 / yyyy") without needing a separate attempt
 * at each. Anything short of a full date returns null, which is what keeps a
 * half-typed date from committing on every keystroke.
 */
export const parseTypeableDate = (text: string): Date | null => {
  const digits = text.replace(/\D/g, "");
  if (digits.length !== 8) return null;

  const parsed = dayjs(digits, "MMDDYYYY", true);
  return parsed.isValid() ? parsed.toDate() : null;
};
